/**
 * Exact guide narration has no microphone input. Keeping it on a separate
 * connection prevents server VAD on the microphone connection from clearing
 * unread narration while the learner's capture is being transcribed.
 */
export type NarrationRealtimeTransport = {
  send: (event: Record<string, unknown>) => boolean;
  close: () => void;
};

export type NarrationRealtimeTransportOptions = {
  model: string;
  clientSecret: string;
  voice: string;
  signal?: AbortSignal;
  onEvent: (event: Record<string, unknown>) => void;
  onError: (error: Error) => void;
};

export type NarrationRealtimeTransportFailureCode = 'cancelled' | 'unsupported' | 'configuration'
  | 'startup_timeout' | 'connection_lost' | 'connection_rejected' | 'send_failed'
  | 'invalid_request' | 'invalid_event' | 'event_processing' | 'provider_transient' | 'provider_rejected';

const FAILURE_MESSAGES: Record<NarrationRealtimeTransportFailureCode, string> = {
  cancelled: 'Voice narration connection was cancelled.',
  unsupported: 'This browser cannot connect voice narration. Please use a supported browser.',
  configuration: 'Voice narration audio configuration was not accepted. Please reconnect.',
  startup_timeout: 'Voice narration connection did not become ready. Please reconnect.',
  connection_lost: 'Voice narration connection ended unexpectedly. Please reconnect.',
  connection_rejected: 'Voice narration connection could not be opened. Please reconnect.',
  send_failed: 'Voice narration request could not be sent. Please reconnect.',
  invalid_request: 'Voice narration request exceeded its limit or could not be encoded. Please reconnect.',
  invalid_event: 'Voice narration received an invalid event. Please reconnect.',
  event_processing: 'Voice narration events could not be processed. Please reconnect.',
  provider_transient: 'Voice narration provider is temporarily unavailable. Please reconnect.',
  provider_rejected: 'Voice narration provider could not complete the request. Please reconnect.',
};
const RETRYABLE_FAILURES = new Set<NarrationRealtimeTransportFailureCode>([
  'startup_timeout', 'connection_lost', 'send_failed', 'provider_transient',
]);

/** Only static, application-owned diagnostics may leave this transport. */
export class NarrationRealtimeTransportError extends Error {
  public readonly retryable: boolean;
  constructor(public readonly code: NarrationRealtimeTransportFailureCode) {
    super(FAILURE_MESSAGES[code]);
    this.name = 'NarrationRealtimeTransportError';
    this.retryable = RETRYABLE_FAILURES.has(code);
  }
}

export const isRetryableNarrationTransportError = (failure: unknown): boolean =>
  failure instanceof NarrationRealtimeTransportError && failure.retryable;

// An unknown provider code, including an auth, quota, policy or validation
// failure, never acquires recovery authority from its message or error type.
const TRANSIENT_PROVIDER_CODES = new Set(['server_error', 'internal_server_error',
  'service_unavailable', 'temporarily_unavailable']);
const TRANSIENT_CLOSE_CODES = new Set([1001, 1005, 1006, 1011, 1012, 1013, 1014]);

const STARTUP_TIMEOUT_MS = 5000;
const MAX_EVENT_LENGTH = 1024 * 1024;
const OUTBOUND_EVENT_TYPES = new Set(['response.create', 'response.cancel', 'conversation.item.truncate']);

const asObject = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;

export const describeNarrationProviderError = (failure: unknown): NarrationRealtimeTransportError => {
  const error = asObject(failure);
  const code = error?.code;
  // A missing code may use the provider's explicit server-error type. An
  // unrecognized/non-string code cannot be overridden by that broad type.
  const transient = typeof code === 'string' ? TRANSIENT_PROVIDER_CODES.has(code)
    : (code === undefined || code === null) && error?.type === 'server_error';
  return new NarrationRealtimeTransportError(transient ? 'provider_transient' : 'provider_rejected');
};

const hasNarrationConfiguration = (event: Record<string, unknown>, options: NarrationRealtimeTransportOptions): boolean => {
  const session = asObject(event.session);
  const audio = asObject(session?.audio);
  const input = asObject(audio?.input);
  const output = asObject(audio?.output);
  const format = asObject(output?.format);
  const modalities = session?.output_modalities;
  return session?.type === 'realtime'
    && session.model === options.model
    && Array.isArray(modalities) && modalities.length === 1 && modalities[0] === 'audio'
    && input?.turn_detection === null && input.transcription === null
    && output?.voice === options.voice
    && format?.type === 'audio/pcm' && format.rate === 24000;
};

export const createNarrationRealtimeTransport = async (
  options: NarrationRealtimeTransportOptions,
): Promise<NarrationRealtimeTransport> => {
  if (options.signal?.aborted) throw new NarrationRealtimeTransportError('cancelled');
  if (typeof WebSocket === 'undefined') throw new NarrationRealtimeTransportError('unsupported');
  if (!options.model.trim() || !options.voice.trim() || !options.clientSecret.trim()) {
    throw new NarrationRealtimeTransportError('configuration');
  }

  return new Promise<NarrationRealtimeTransport>((resolve, reject) => {
    let socket: WebSocket | null = null;
    let closed = false;
    let ready = false;
    let configurationSent = false;
    let startupTimer: ReturnType<typeof setTimeout> | undefined;

    const clearStartupTimer = () => {
      if (startupTimer !== undefined) clearTimeout(startupTimer);
      startupTimer = undefined;
    };
    const cleanup = () => {
      closed = true;
      clearStartupTimer();
      options.signal?.removeEventListener('abort', abort);
      if (!socket) return;
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) {
        try { socket.close(); } catch { /* The socket may already be closing. */ }
      }
    };
    const fail = (failure: NarrationRealtimeTransportFailureCode | NarrationRealtimeTransportError) => {
      if (closed) return;
      const wasReady = ready;
      cleanup();
      const error = failure instanceof NarrationRealtimeTransportError ? failure : new NarrationRealtimeTransportError(failure);
      if (!wasReady) reject(error);
      else {
        // A consumer failure must never expose the original event or escape a
        // native socket callback as an unhandled exception.
        try { options.onError(error); } catch { /* The connection is already retired. */ }
      }
    };
    const close = () => {
      if (closed) return;
      const wasReady = ready;
      cleanup();
      if (!wasReady) reject(new NarrationRealtimeTransportError('cancelled'));
    };
    const abort = () => close();
    const transport: NarrationRealtimeTransport = {
      send: (event) => {
        if (closed || !ready || typeof event.type !== 'string' || !OUTBOUND_EVENT_TYPES.has(event.type)) return false;
        if (socket?.readyState !== WebSocket.OPEN) {
          // A send can beat the native close callback. Keep the same typed
          // network failure rather than turning that race into a local fault.
          fail('connection_lost');
          return false;
        }
        let payload: string;
        try {
          payload = JSON.stringify(event);
          if (payload.length > MAX_EVENT_LENGTH) {
            fail('invalid_request');
            return false;
          }
        } catch {
          fail('invalid_request');
          return false;
        }
        try {
          socket.send(payload);
          return true;
        } catch (failure) {
          const name = failure instanceof Error ? failure.name : '';
          fail(['SecurityError', 'SyntaxError', 'NotSupportedError'].includes(name) ? 'configuration' : 'send_failed');
          return false;
        }
      },
      close,
    };

    try {
      socket = new WebSocket(`wss://api.openai.com/v1/realtime?model=${encodeURIComponent(options.model)}`, [
        'realtime', `openai-insecure-api-key.${options.clientSecret}`,
      ]);
      options.signal?.addEventListener('abort', abort, { once: true });
      startupTimer = setTimeout(() => fail('startup_timeout'), STARTUP_TIMEOUT_MS);
      socket.onopen = () => {
        if (closed || !socket || configurationSent) return;
        try {
          configurationSent = true;
          socket.send(JSON.stringify({
            type: 'session.update',
            session: {
              type: 'realtime',
              output_modalities: ['audio'],
              audio: {
                input: { turn_detection: null, transcription: null },
                output: { format: { type: 'audio/pcm', rate: 24000 }, voice: options.voice },
              },
            },
          }));
        } catch (failure) {
          const name = failure instanceof Error ? failure.name : '';
          fail(['SecurityError', 'SyntaxError', 'NotSupportedError'].includes(name) ? 'configuration' : 'send_failed');
        }
      };
      socket.onmessage = ({ data }: MessageEvent<unknown>) => {
        if (closed) return;
        let event: Record<string, unknown> | null;
        try {
          if (typeof data !== 'string' || data.length > MAX_EVENT_LENGTH) {
            fail('invalid_event');
            return;
          }
          event = asObject(JSON.parse(data));
          if (!event || typeof event.type !== 'string' || event.type.length === 0) {
            fail('invalid_event');
            return;
          }
        } catch {
          fail('invalid_event');
          return;
        }
        if (event.type === 'error') {
          if (ready && asObject(event.error)?.code === 'response_cancel_not_active') {
            // Cancellation may reach the provider after its owned response has
            // ended. Preserve that exact harmless race without its raw body.
            event = { type: 'error', error: { code: 'response_cancel_not_active' } };
          } else {
            fail(describeNarrationProviderError(event.error));
            return;
          }
        }
        if (event.type === 'session.updated') {
          if (!configurationSent) return;
          if (!hasNarrationConfiguration(event, options)) {
            fail('configuration');
            return;
          }
          if (!ready) {
            ready = true;
            clearStartupTimer();
            resolve(transport);
            return;
          }
        }
        if (!ready) return;
        try { options.onEvent(event); } catch {
          fail('event_processing');
        }
      };
      socket.onerror = () => fail('connection_lost');
      socket.onclose = (event: CloseEvent) => fail(TRANSIENT_CLOSE_CODES.has(event.code) ? 'connection_lost' : 'connection_rejected');
      if (options.signal?.aborted) close();
    } catch {
      fail('connection_rejected');
    }
  });
};
